import { Component, Prop, State, h, Build, Listen, Element } from '@stencil/core';

@Component({
  tag: 'textyess-bubble-widget',
  styleUrl: 'textyess-bubble-widget.css',
  shadow: true,
})
export class TextyessBubbleWidget {
  @Element() el: HTMLElement;

  @Prop() phone_number: string;
  @Prop() default_message: string;
  @Prop() user_id: string;
  @Prop() bubble_color: string;
  @Prop() bubble_content_color: string;
  @Prop() initial_message: string;
  @Prop() box_bg_color: string = '#f5f7f9';
  @Prop() position: 'left' | 'right' = 'right';
  @Prop() cta: string = 'Contact us';
  @Prop() cta_content_color: string = '#fff';
  @Prop() cta_color: string = '#503ADC';
  @Prop() box_color: string = '#000';

  @State() baseLink: string;
  @State() baseUrl: string;
  @State() uid: string;
  @State() shouldShowLogo: boolean = false;
  @State() isOpen: boolean = false;
  @State() lastTouchEventTime: number = 0;
  @State() isDebouncingToggle: boolean = false;

  @Listen('toggleOpen')
  componentWillLoad() {
    if (Build.isDev) {
      this.baseUrl = 'https://api-ai.textyess.com';
    } else {
      this.baseUrl = 'https://api-ai.textyess.com';
    }
    this.fetchData();
  }

  async fetchData() {
    try {
      this.uid = window.localStorage.getItem('textyess-uid') || '';
      const response = await fetch(`${this.baseUrl}/widget/whatsapp/${this.user_id}/read?widget=bubble&uid=${this.uid}`);
      const data = await response.json();
      this.baseLink = data?.link;
      console.log(this.baseLink);
      this.shouldShowLogo = data?.show_widget_logo;
      this.uid = data?.uid;
      window.localStorage.setItem('textyess-uid', this.uid);
    } catch (error) {
      console.error('Error fetching the data', error);
    }
  }

  private getWhatsAppLink(): string {
    let whatsAppLink = this.baseLink;
    if (this.default_message && this.default_message !== 'undefined') {
      const encodedText = encodeURIComponent(this.default_message);
      whatsAppLink += `?text=${encodedText}`;
    }
    console.log('whatsAppLink', whatsAppLink);
    return whatsAppLink;
  }

  componentDidLoad() {
    this.setupEventListeners();
  }

  componentDidUpdate() {
    // Re-setup event listeners to ensure they are still correctly attached
    // after any updates that might have affected the DOM.
    this.setupEventListeners();
  }

  setupEventListeners() {
    const chatBubbleShadow = this.el.shadowRoot.querySelector('#chat-bubble-shadow');

    // Handle interactions on the chat bubble shadow
    const handleBubbleInteraction = event => {
      // Prevent toggle if the event is from specific child elements
      if (event.target.closest('.cta, .powered-by')) {
        return; // Early return to avoid toggling
      }
      event.preventDefault();
      this.handleToggle();
    };

    // Apply the correct event listener based on device capability
    if ('ontouchstart' in window) {
      chatBubbleShadow.addEventListener('touchstart', handleBubbleInteraction, { passive: false });
    } else {
      chatBubbleShadow.addEventListener('click', handleBubbleInteraction);
    }

    setTimeout(() => {
      // THIS NEEDED TO BE DELETED BECAUSE OF
      // const ctaButton = this.el.shadowRoot.querySelector('.cta');
      // if (ctaButton) {
      //   ctaButton.addEventListener('click', () => window.open(this.getWhatsAppLink(), '_blank'));
      // }

      const poweredByLink = this.el.shadowRoot.querySelector('.powered-by');
      if (poweredByLink) {
        poweredByLink.addEventListener('click', () => window.open(`https://www.textyess.com?utm_campaign=powered_by&utm_medium=bubble&utm_source=${this.user_id}`, '_blank'));
      }
    }, 0);
  }

  handleToggle() {
    // Check if we're currently debouncing subsequent calls
    if (this.isDebouncingToggle) {
      return;
    }

    // Set the debounce flag
    this.isDebouncingToggle = true;

    // Proceed to toggle the state
    this.isOpen = !this.isOpen;
    const toggleEvent = new CustomEvent('toggleOpen', { detail: this.isOpen });
    this.el.dispatchEvent(toggleEvent);

    // Clear the debounce flag after a short delay
    setTimeout(() => {
      this.isDebouncingToggle = false;
    }, 300); // 300ms debounce period
  }

  render() {
    const wrapperStyle = {
      '--wrapper-horizontal-position': this.position === 'left' ? 'auto' : '1rem',
    };
    return (
      <div id="whatsapp-link">
        <div class="wrapper" style={wrapperStyle}>
          {this?.isOpen && (
            <div class="logo-message-wrapper">
              <div class="message-box" style={{ backgroundColor: this?.box_bg_color }}>
                <div class="text-cta">
                  <p class="paragraph" style={{ color: this.box_color }}>
                    {this.initial_message}
                  </p>
                  <button
                    class="cta"
                    onClick={e => {
                      e.preventDefault();
                      const link = this.getWhatsAppLink();
                      window.open(link, '_blank');
                    }}
                    style={{ color: this?.cta_content_color, backgroundColor: this.cta_color }}
                  >
                    {this.cta}
                  </button>
                </div>
                {this.shouldShowLogo && (
                  <div
                    class="powered-by"
                    onClick={() => {
                      window.open(`https://www.textyess.com?utm_campaign=powered_by&utm_medium=bubble&utm_source=${this.user_id}`, '_blank');
                    }}
                  >
                    <span>Powered by</span>
                    <button class="logo-button">
                      <svg width="90" height="11.3" viewBox="0 0 544 113" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M471.181 38.4546H448.091C447.969 37.0304 447.469 35.8031 446.591 34.7728C445.712 33.7425 444.575 32.9546 443.181 32.4091C441.818 31.8334 440.303 31.5455 438.636 31.5455C436.484 31.5455 434.636 31.9394 433.091 32.7273C431.545 33.5152 430.788 34.6364 430.818 36.091C430.788 37.1213 431.227 38.0758 432.136 38.9546C433.075 39.8334 434.878 40.5152 437.545 41.0001L451.727 43.5455C458.878 44.8485 464.197 47.0455 467.681 50.1364C471.197 53.197 472.969 57.3031 473 62.4546C472.969 67.4243 471.484 71.7425 468.545 75.4091C465.636 79.0455 461.651 81.8637 456.591 83.8637C451.56 85.8334 445.818 86.8182 439.363 86.8182C428.697 86.8182 420.348 84.6364 414.318 80.2728C410 77 408 71.5 408 71L429.181 63.0001C429.515 65.2122 430.606 66.9243 432.454 68.1364C434.333 69.3182 436.697 69.9091 439.545 69.9091C441.848 69.9091 443.742 69.5152 445.227 68.7273C446.742 67.9394 447.515 66.8182 447.545 65.3637C447.515 64.0304 446.848 62.9697 445.545 62.1819C444.272 61.394 442.272 60.7576 439.545 60.2728L427.181 58.091C420.06 56.8485 414.727 54.4849 411.181 51.0001C407.636 47.5152 405.878 43.0304 405.909 37.5455C405.878 32.697 407.151 28.591 409.727 25.2273C412.333 21.8334 416.045 19.2576 420.863 17.5001C425.712 15.7122 431.454 14.8182 438.091 14.8182C448.181 14.8182 456.136 16.9091 461.954 21.091C467.803 25.2728 470.878 31.0607 471.181 38.4546Z"
                          fill="#503ADC"
                        />
                        <path
                          d="M541.273 38.4546H518.182C518.061 37.0304 517.561 35.8031 516.682 34.7728C515.804 33.7425 514.667 32.9546 513.273 32.4091C511.91 31.8334 510.394 31.5455 508.728 31.5455C506.576 31.5455 504.728 31.9394 503.182 32.7273C501.637 33.5152 500.879 34.6364 500.91 36.091C500.879 37.1213 501.319 38.0758 502.228 38.9546C503.167 39.8334 504.97 40.5152 507.637 41.0001L521.819 43.5455C528.97 44.8485 534.288 47.0455 537.773 50.1364C541.288 53.197 543.061 57.3031 543.091 62.4546C543.061 67.4243 541.576 71.7425 538.637 75.4091C535.728 79.0455 531.743 81.8637 526.682 83.8637C521.652 85.8334 515.91 86.8182 509.455 86.8182C498.788 86.8182 490.44 84.6364 484.41 80.2728C480.092 77 478.092 71.5 478.092 71L499.273 63.0001C499.607 65.2122 500.697 66.9243 502.546 68.1364C504.425 69.3182 506.788 69.9091 509.637 69.9091C511.94 69.9091 513.834 69.5152 515.319 68.7273C516.834 67.9394 517.607 66.8182 517.637 65.3637C517.607 64.0304 516.94 62.9697 515.637 62.1819C514.364 61.394 512.364 60.7576 509.637 60.2728L497.273 58.091C490.152 56.8485 484.819 54.4849 481.273 51.0001C477.728 47.5152 475.97 43.0304 476.001 37.5455C475.97 32.697 477.243 28.591 479.819 25.2273C482.425 21.8334 486.137 19.2576 490.955 17.5001C495.804 15.7122 501.546 14.8182 508.182 14.8182C518.273 14.8182 526.228 16.9091 532.046 21.091C537.894 25.2728 540.97 31.0607 541.273 38.4546Z"
                          fill="#503ADC"
                        />
                        <path
                          d="M289.637 112.727C286.758 112.727 284.015 112.5 281.409 112.045C278.803 111.621 276.515 111.03 274.546 110.273L280 92.4546C282.061 93.1818 283.94 93.6515 285.637 93.8637C287.364 94.0758 288.834 93.9546 290.046 93.5C291.288 93.0455 292.182 92.1818 292.728 90.9091L293.273 89.6364L268.728 16.7273H294.909L306.182 65.0909H306.909L318.364 16.7273H344.728L319.273 92.3637C318 96.2121 316.152 99.6667 313.728 102.727C311.334 105.818 308.182 108.258 304.273 110.045C300.364 111.833 295.485 112.727 289.637 112.727Z"
                          fill="#503ADC"
                        />
                        <path
                          d="M229 16.7273V34.9091H183V16.7273H229ZM191.909 0H217V64.0909C217 65.0606 217.167 65.8788 217.5 66.5455C217.833 67.1818 218.348 67.6667 219.045 68C219.742 68.303 220.636 68.4545 221.727 68.4545C222.485 68.4545 223.364 68.3636 224.364 68.1818C225.394 68 226.152 67.8485 226.636 67.7273L230.273 85.3636C229.152 85.697 227.545 86.1061 225.455 86.5909C223.394 87.0758 220.939 87.3939 218.091 87.5455C212.333 87.8485 207.5 87.2576 203.591 85.7727C199.682 84.2576 196.742 81.8788 194.773 78.6364C192.803 75.3939 191.848 71.3333 191.909 66.4545V0Z"
                          fill="#503ADC"
                        />
                        <path
                          d="M135 16.7273L145.182 38.3637L155.909 16.7273H180.819L162.091 51.6364L181.728 86.5455H157L145.182 64.3637L133.728 86.5455H108.637L128.455 51.6364L109.909 16.7273H135Z"
                          fill="#503ADC"
                        />
                        <path
                          d="M46 16.7273V34.9091H0V16.7273H46ZM8.90909 0H34V64.0909C34 65.0606 34.1667 65.8788 34.5 66.5455C34.8333 67.1818 35.3485 67.6667 36.0455 68C36.7424 68.303 37.6364 68.4545 38.7273 68.4545C39.4848 68.4545 40.3636 68.3636 41.3636 68.1818C42.3939 68 43.1515 67.8485 43.6364 67.7273L47.2727 85.3636C46.1515 85.697 44.5455 86.1061 42.4545 86.5909C40.3939 87.0758 37.9394 87.3939 35.0909 87.5455C29.3333 87.8485 24.5 87.2576 20.5909 85.7727C16.6818 84.2576 13.7424 81.8788 11.7727 78.6364C9.80303 75.3939 8.84848 71.3333 8.90909 66.4545V0Z"
                          fill="#503ADC"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M79.7562 68.0542C79.0777 67.9694 78.3993 67.821 77.7208 67.609L110.286 28.3652C108.675 26.0754 106.809 24.0189 104.689 22.1956C102.569 20.3299 100.279 18.761 97.8198 17.4889C95.4028 16.2168 92.8375 15.2415 90.1237 14.5631C87.4099 13.8846 84.6325 13.5454 81.7915 13.5454C77 13.5454 72.4841 14.4147 68.2438 16.1532C64.0459 17.8917 60.3569 20.3299 57.1767 23.4677C54.0389 26.6055 51.5583 30.4005 49.735 34.8528C47.9117 39.3051 47 44.2239 47 49.609C47 54.867 47.9117 59.7009 49.735 64.1108C51.5583 68.4783 54.0389 72.2521 57.1767 75.4323C60.3569 78.6125 64.0459 81.0931 68.2438 82.874C72.4841 84.655 77 85.5454 81.7915 85.5454C84.6325 85.5454 87.3887 85.2062 90.0601 84.5277C92.7738 83.8917 95.3392 82.9376 97.7562 81.6656C100.216 80.3935 102.484 78.8458 104.562 77.0224C106.682 75.1991 108.548 73.1426 110.159 70.8528L97.4382 58.0684C96.7597 59.5949 95.8693 60.9942 94.7668 62.2663C93.7067 63.4959 92.477 64.556 91.0777 65.4465C89.7208 66.2945 88.2579 66.9518 86.689 67.4182C85.1201 67.8846 83.4876 68.1179 81.7915 68.1179C81.1131 68.1179 80.4346 68.0966 79.7562 68.0542ZM83.9542 30.973C84.7599 31.0578 85.5655 31.2486 86.3712 31.5454L66.145 58.3864C65.8058 57.7928 65.5302 57.1143 65.3182 56.3511C65.1062 55.5878 64.9154 54.8034 64.7458 53.9977C64.6185 53.1496 64.5125 52.3228 64.4277 51.5171C64.3853 50.7115 64.3641 49.9906 64.3641 49.3546C64.3641 46.5136 64.8094 43.9694 65.6998 41.7221C66.5903 39.4323 67.7988 37.4818 69.3253 35.8705C70.8942 34.2592 72.7175 33.0295 74.7952 32.1814C76.9154 31.291 79.1627 30.8458 81.5373 30.8458C82.3853 30.8458 83.191 30.8882 83.9542 30.973Z"
                          fill="#503ADC"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M372.756 68.0542C372.078 67.9694 371.399 67.821 370.721 67.609L403.286 28.3652C401.675 26.0754 399.809 24.0189 397.689 22.1956C395.569 20.3299 393.279 18.761 390.82 17.4889C388.403 16.2168 385.837 15.2415 383.124 14.5631C380.41 13.8846 377.633 13.5454 374.792 13.5454C370 13.5454 365.484 14.4147 361.244 16.1532C357.046 17.8917 353.357 20.3299 350.177 23.4677C347.039 26.6055 344.558 30.4005 342.735 34.8528C340.912 39.3051 340 44.2239 340 49.609C340 54.867 340.912 59.7009 342.735 64.1108C344.558 68.4783 347.039 72.2521 350.177 75.4323C353.357 78.6125 357.046 81.0931 361.244 82.874C365.484 84.655 370 85.5454 374.792 85.5454C377.633 85.5454 380.389 85.2062 383.06 84.5277C385.774 83.8917 388.339 82.9376 390.756 81.6656C393.216 80.3935 395.484 78.8458 397.562 77.0224C399.682 75.1991 401.548 73.1426 403.159 70.8528L390.438 58.0684C389.76 59.5949 388.869 60.9942 387.767 62.2663C386.707 63.4959 385.477 64.556 384.078 65.4465C382.721 66.2945 381.258 66.9518 379.689 67.4182C378.12 67.8846 376.488 68.1179 374.792 68.1179C374.113 68.1179 373.435 68.0966 372.756 68.0542ZM376.954 30.973C377.76 31.0578 378.566 31.2486 379.371 31.5454L359.145 58.3864C358.806 57.7928 358.53 57.1143 358.318 56.3511C358.106 55.5878 357.915 54.8034 357.746 53.9977C357.619 53.1496 357.513 52.3228 357.428 51.5171C357.385 50.7115 357.364 49.9906 357.364 49.3546C357.364 46.5136 357.809 43.9694 358.7 41.7221C359.59 39.4323 360.799 37.4818 362.325 35.8705C363.894 34.2592 365.717 33.0295 367.795 32.1814C369.915 31.291 372.163 30.8458 374.537 30.8458C375.385 30.8458 376.191 30.8882 376.954 30.973Z"
                          fill="#503ADC"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div id="chat-bubble-shadow">
            <div id="chat-bubble">
              <svg enable-background="new 0 0 128 128" height="56px" id="Layer_1" version="1.1" viewBox="0 0 128 128" width="56px" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <filter id="shadow" x="-25%" y="-25%" width="150%" height="150%">
                    <feOffset result="offOut" in="SourceAlpha" dx="0" dy="2" />
                    <feGaussianBlur result="blurOut" in="offOut" stdDeviation="4" />
                    <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
                    <feComponentTransfer>
                      <feFuncA type="linear" slope="0.2"></feFuncA>
                    </feComponentTransfer>
                    <feMerge>
                      <feMergeNode />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <circle cx="64" cy="64" fill={this?.bubble_color ? this?.bubble_color : '#25D366'} r="56" filter="url(#shadow)" />
                <g transform="scale(0.875) translate(8,8)">
                  <g filter="url(#shadow)">
                    <path
                      d="M92.346,35.49c-7.522-7.53-17.523-11.678-28.179-11.683c-21.954,0-39.826,17.868-39.833,39.831   c-0.004,7.022,1.831,13.875,5.316,19.913L24,104.193l21.115-5.538c5.819,3.171,12.369,4.844,19.036,4.847h0.017l0,0   c21.954,0,39.823-17.871,39.832-39.833C104.005,53.027,99.864,43.019,92.346,35.49 M64.168,96.774h-0.013   c-5.943-0.002-11.769-1.598-16.853-4.614l-1.209-0.718l-12.53,3.287l3.343-12.216l-0.787-1.256   c-3.315-5.27-5.066-11.361-5.062-17.619c0.006-18.253,14.859-33.104,33.121-33.104c8.844,0.002,17.155,3.451,23.407,9.71   c6.251,6.258,9.691,14.575,9.689,23.422C97.268,81.922,82.415,96.774,64.168,96.774 M82.328,71.979   c-0.996-0.499-5.889-2.904-6.802-3.239c-0.913-0.332-1.574-0.497-2.238,0.499s-2.571,3.239-3.153,3.903   c-0.58,0.664-1.16,0.748-2.156,0.249s-4.202-1.549-8.001-4.941c-2.96-2.637-4.958-5.899-5.538-6.895s-0.062-1.533,0.437-2.03   c0.448-0.446,0.996-1.162,1.493-1.744c0.497-0.582,0.663-0.997,0.995-1.66c0.332-0.664,0.167-1.245-0.083-1.743   c-0.25-0.499-2.24-5.398-3.068-7.391c-0.809-1.941-1.629-1.678-2.239-1.708c-0.582-0.028-1.245-0.036-1.908-0.036   c-0.663,0-1.742,0.249-2.654,1.246c-0.911,0.996-3.483,3.403-3.483,8.304c0,4.898,3.566,9.632,4.064,10.295   c0.498,0.663,7.018,10.718,17.002,15.029c2.374,1.024,4.229,1.637,5.674,2.097c2.384,0.759,4.554,0.65,6.27,0.394   c1.912-0.285,5.888-2.407,6.719-4.732c0.829-2.324,0.829-4.316,0.578-4.732C83.986,72.727,83.322,72.478,82.328,71.979"
                      fill={this?.bubble_content_color ? this?.bubble_content_color : '#FFFFFF'}
                    />
                  </g>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
